import { useDialog, type AriaDialogProps } from '@react-aria/dialog'
import { DismissButton } from '@react-aria/overlays'
import { mergeProps } from '@react-aria/utils'
import type { FocusableElement } from '@react-types/shared'
import { useMemo, useRef, type DOMAttributes, type ReactNode, type RefObject } from 'react'
import { RemoveScroll } from 'react-remove-scroll'

import { usePopoverContext } from './PopoverContext'

type TPopoverContentProps = AriaDialogProps & {
  children: ReactNode | ((titleProps: DOMAttributes<HTMLElement>) => ReactNode)
  className?: string
  testID?: string
}

export const PopoverContent = (props: TPopoverContentProps) => {
  const { children, className, ...otherProps } = props

  const {
    Component: OverlayComponent,
    backdrop,
    isOpen,
    shouldBlockScroll,
    getPopoverProps,
    getDialogProps,
    getBackdropProps,
    getContentProps,
    isNonModal,
    onClose,
  } = usePopoverContext()

  const Component = OverlayComponent || 'div'

  // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- ref bridge
  const dialogRef = useRef<FocusableElement>(null) as RefObject<FocusableElement>
  const { dialogProps, titleProps } = useDialog({}, dialogRef)

  // Not needed in the popover context, the popover role comes from getPopoverProps
  const { role: _, ...dialogPropsWithoutRole } = dialogProps

  const content = (
    <>
      {!isNonModal && <DismissButton onDismiss={onClose} />}
      <Component {...getDialogProps(mergeProps(dialogPropsWithoutRole, otherProps))} ref={dialogRef}>
        <div {...getContentProps({ className })}>
          {typeof children === 'function' ? children(titleProps) : children}
        </div>
      </Component>
      <DismissButton onDismiss={onClose} />
    </>
  )

  const backdropContent = useMemo(() => {
    if (backdrop === 'transparent') {
      return null
    }
    return <div {...getBackdropProps()} />
  }, [backdrop, getBackdropProps])

  const blockScroll = shouldBlockScroll ? isOpen : null
  return (
    <div {...getPopoverProps()}>
      {backdropContent}
      <RemoveScroll forwardProps enabled={!!blockScroll} removeScrollBar={false}>
        {content}
      </RemoveScroll>
    </div>
  )
}
